/**
 * CameraConfig.js - Centralized camera configuration and constants
 * Configuration for Saxavord Spaceport scene
 */

import * as THREE from 'three/webgpu'

// ============================================================================
// DEFAULT CAMERA POSITIONS
// ============================================================================

export const DEFAULT_CAMERA_POSITIONS = {
  scene1: {
    landscape: {
      // Saxavord scene default camera position - from saxhq.glb CAM_DEFAULT and TAR_DEFAULT
      position: new THREE.Vector3(-7, 5, 5),
      target: new THREE.Vector3(0, 0, -7)
    },
    portrait: {
      // Saxavord scene portrait - slightly adjusted for vertical framing
      position: new THREE.Vector3(-5.799, 9, 15.485),
      target: new THREE.Vector3(0.946, 1.5, -10.776)
    }
  }
}

/**
 * Get default camera position for a scene based on orientation
 * @param {string} scene - 'scene1'
 * @param {boolean} isLandscape - True for landscape orientation, false for portrait
 * @returns {Object} {position: Vector3, target: Vector3}
 */
export function getDefaultCameraPosition(scene, isLandscape = true) {
  const orientation = isLandscape ? 'landscape' : 'portrait'
  return DEFAULT_CAMERA_POSITIONS[scene][orientation]
}

// ============================================================================
// CAMERA CONTROLS DEFAULTS
// ============================================================================

export const CAMERA_CONTROLS_DEFAULTS = {
  minDistance: 0.1,              // Allow very close zoom
  maxDistance: 200,              // Allow very far zoom
  enableZoom: true,              // Enable zoom
  enablePan: false,              // Keep panning disabled
  smoothTime: 0.1,
  // Scene 1 default limits - Saxavord scene with ±15% rotation from default
  // Default camera: position(-5.799, 7.407, 15.485) looking at (0.946, 0.747, -10.776)
  // Calculated angles: azimuth ≈ -0.6866 rad, polar ≈ 1.326 rad
  // 15% range: azimuth ±0.103 rad (±5.9°), polar ±0.199 rad (±11.4°)
  scene1: {
    maxPolarAngle: 1.326 + 0.15,     // Allow 15% up
    minPolarAngle: 1.326 - 0.15,     // Allow 15% down
    maxAzimuthAngle: -0.6866 + 1.40, // Allow 15% right
    minAzimuthAngle: -0.6866 - 0.15  // Allow 15% left
  }
}

// ============================================================================
// PER-POI CAMERA ROTATION LIMITS
// ============================================================================

/**
 * Per-POI camera rotation limits
 * Each POI can have its own polar and azimuth angle constraints
 */
export const POI_CAMERA_LIMITS = {
  POI_HA1: {
    maxPolarAngle: 1.326 + 0.15,
    minPolarAngle: 1.326 - 0.15,
    maxAzimuthAngle: 1.5,
    minAzimuthAngle: -0.5
  },
  POI_LP1: {
    maxPolarAngle: 1.326 + 0.15,
    minPolarAngle: 1.326 - 0.15,
    maxAzimuthAngle: -1,
    minAzimuthAngle: -3
  },
  POI_SAT: {
    maxPolarAngle: 1.326 + 0.15,
    minPolarAngle: 1.326 - 0.15,
    maxAzimuthAngle: -0.6866 + 1.40,
    minAzimuthAngle: -0.6866 - 0.15
  },
  POI_MET: {
    maxPolarAngle: 1.326 + 0.15,
    minPolarAngle: 1.326 - 0.15,
    maxAzimuthAngle: -0.6866 + 1.40,
    minAzimuthAngle: -0.6866 - 0.15
  },
  POI_RAF: {
    maxPolarAngle: 1.326 + 0.0,
    minPolarAngle: 1.326 - 0.15,
    maxAzimuthAngle: -0.6866 + 1.40,
    minAzimuthAngle: -0.6866 - 0.15
  },
  POI_HIS: {
    maxPolarAngle: 1.326 + 0.15,
    minPolarAngle: 1.326 - 0.15,
    maxAzimuthAngle: -0.6866 + 1.40,
    minAzimuthAngle: -0.6866 - 0.15
  }
}

/**
 * Get camera rotation limits for a specific POI
 * @param {string} poiName - POI identifier (e.g., 'POI_HA1', 'POI_LP1', 'POI_SAT')
 * @returns {Object|null} Limit configuration or null if POI has no custom limits
 */
export function getPOICameraLimits(poiName) {
  return POI_CAMERA_LIMITS[poiName] || null
}

// ============================================================================
// MOBILE PORTRAIT POI LOOK-AT OVERRIDES
// ============================================================================

/**
 * Per-POI look-at target overrides for mobile portrait (≤767px, portrait orientation).
 * Camera position remains the same as desktop — only the look-at target is overridden.
 * Values are initialised to match the existing TAR_* model nodes; adjust as needed.
 */
export const MOBILE_PORTRAIT_POI_TARGETS = {
  POI_HA1: new THREE.Vector3(2.3, 0.0, -9.506),    // matches TAR_HA
  POI_LP1: new THREE.Vector3(-2.75, 0.2, -8.8),    // matches TAR_LP
  POI_SAT: new THREE.Vector3(1.2, -0.085, -4.2),   // matches TAR_SA
  POI_MET: null,                                    // TAR_MET not in model
  POI_RAF: new THREE.Vector3(0.1, 0.05, -10.5),    // matches TAR_RAF
  POI_HIS: new THREE.Vector3(2.533, 0.066, -2.32), // matches TAR_HIS
}

// ============================================================================
// ANIMATION TIMINGS
// ============================================================================

export const ANIMATION_TIMINGS = {
  cameraMovement: 2000,      // ms - standard camera movement duration
  orientationChange: 0       // ms - immediate camera update when orientation changes
}
